import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon, Typography } from '@packages/ui/shared';
import { getLogger } from '@packages/core/logging';
import TechShareLogo from '@packages/ui/assets/images/techshare-logo.svg?react';
import EmergencyGuideBanner from './emergency-guide-banner';
import thumbnail from '@web/vin-sessions/assets/Thumbnail.png';
import { createUseStyles } from 'react-jss';
import colors from '@packages/core/styles/colors';
import { QuickReferenceCard } from '@web/vin-sessions/components/reference-card';
import { StyleSheet } from 'react-native';
import { configService, docsService, vinSessionService } from '@web/services/singletons';
import { handleVinSessionError } from '@web/vin-sessions/utils/vinSessionUtils';
import { SkeletonLoader } from '@web/components/skeleton-loader';
import { getUrl } from '@web/vin-sessions/components/stis-publications/stis-table';
import { usePublicationsDispatch } from '@web/vin-sessions/components/stis-publications/context/publications.hooks';
import { publicationsActions } from '@web/vin-sessions/components/stis-publications/context/publications.state';

const logger = getLogger('web');

const useStyles = createUseStyles({
    root: {
        padding: 20,
    },
    container: {
        padding: '24px 80px 60px 24px',
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
    },
    banner: {
        paddingBottom: 24,
    },
    description: {
        paddingBottom: 24,
    },
    section: {},
    filterRow: {
        display: 'flex',
        justifyContent: 'space-between',
        gap: 18,
        marginBottom: 24,
        width: '60%',
    },
    searchContainer: {
        position: 'relative',
        flex: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '40%',
    },
    searchInput: {
        width: '100%',
        padding: '6px 32px 6px 8px',
        border: '1px solid #ccc',
        borderRadius: 4,
        fontSize: 14,
    },
    searchIcon: {
        position: 'absolute',
        right: 8,
        top: '50%',
        transform: 'translateY(-50%)',
        pointerEvents: 'none',
        color: '#888',
        display: 'flex',
        alignItems: 'center',
    },
    dropdowns: {
        display: 'flex',
        width: '50%',
        gap: 32,
    },
    dropdown: {
        position: 'relative',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        transition: 'width 0.2s, background 0.2s',
        padding: 4,
        borderRadius: '4px 0 0 4px',
        minWidth: 0,
        backgroundColor: 'transparent',
    },
    dropdownActive: {
        backgroundColor: '#DEE6EF',
    },
    dropdownMenu: {
        position: 'absolute',
        top: '100%',
        left: 0,
        background: '#fff',
        border: '1px solid #ccc',
        borderRadius: 4,
        boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
        zIndex: 10,
        minWidth: 180,
        padding: 8,
        width: 200,
        maxHeight: 240,
        overflowY: 'auto',
    },
    dropdownMenuModelYear: {
        borderRadius: '4px 0 0 4px',
    },
    dropdownLabel: {
        display: 'flex',
        alignItems: 'center',
        cursor: 'pointer',
        width: '100%',
    },
    dropdownArrow: {
        position: 'absolute',
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        transition: 'left 0.2s, right 0.2s, transform 0.2s',
    },
    mainContent: {
        display: 'flex',
        width: '100%',
        minHeight: 320,
        maxHeight: 420,
        height: 420,
        borderRadius: 8,
        overflow: 'hidden',
        boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
        border: '1px solid #E0E4EA',
    },
    carlineColumn: {
        width: '26%',
        background: '#F3F3F3',
        padding: 0,
        overflowY: 'auto',
        maxHeight: '100%',
        scrollbarWidth: 'thin',
        scrollbarColor: '#1F518F #FFF',
    },
    carlineRow: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '20px 24px',
        background: '#F3F3F3',
        cursor: 'pointer',
        borderBottom: '1px solid #E0E4EA',
        transition: 'background 0.2s',
    },
    carlineRowSelected: {
        background: '#fff',
    },
    carlineTypography: {
        fontWeight: 500,
        fontSize: 17,
        color: '#4C525D',
    },
    carlineIcon: {
        transform: 'rotate(90deg)',
    },
    rightColumn: {
        width: '74%',
        background: '#fff',
        padding: '24px 32px',
        overflowY: 'auto',
        maxHeight: '100%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
    },
    referenceCardContainer: {
        padding: 24,
        display: 'flex',
        gap: 16,
        overflowX: 'auto',
        scrollBehavior: 'smooth',
        scrollbarWidth: 'none',
        '&::-webkit-scrollbar': {
            display: 'none',
        },
    },
    scrollButton: {
        position: 'absolute',
        top: '50%',
        bottom: '8px',
        transform: 'translateY(-50%)',
        width: 40,
        height: 40,
        backgroundColor: colors.blueOne,
        border: 'none',
        borderRadius: 4,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        zIndex: 2,
        '&:hover': {
            backgroundColor: '#0056b3',
        },
    },
    scrollButtonLeft: {
        left: '16px',
    },
    scrollButtonRight: {
        right: '8px',
    },
    gradientOverlay: {
        position: 'absolute',
        top: 0,
        bottom: 0,
        width: 120,
        zIndex: 1,
        pointerEvents: 'none',
    },
    gradientLeft: {
        left: 0,
        background: 'linear-gradient(to right, rgba(255, 255, 255, 0.9), rgba(255, 255, 255, 0))',
    },
    gradientRight: {
        right: 0,
        background: 'linear-gradient(to left, rgba(255, 255, 255, 0.9), rgba(255, 255, 255, 0))',
    },
    // Custom scrollbar for carline column
    '@global': {
        '.custom-scrollbar::-webkit-scrollbar': {
            width: 10,
        },
        '.custom-scrollbar::-webkit-scrollbar-track': {
            backgroundColor: '#ffffff',
        },
        '.custom-scrollbar::-webkit-scrollbar-thumb': {
            backgroundColor: colors.blueFour,
            borderRadius: 10,
            border: '2px solid #ffffff',
        },
        '.custom-scrollbar::-webkit-scrollbar-thumb:hover': {
            cursor: 'pointer',
            backgroundColor: colors.blueOne,
        },
    },
});

type ReferenceDataItem = {
    icon: JSX.Element;
    title: string;
    image: typeof thumbnail;
    version: string;
    id: string;
};

export const EmergencyGuides = () => {
    const [showLeftButton, setShowLeftButton] = useState(false);
    const [showRightButton, setShowRightButton] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedCarline, setSelectedCarline] = useState<string[]>([]);
    const [selectedModelYear, setSelectedModelYear] = useState<string[]>([]);
    const [showCarlineDropdown, setShowCarlineDropdown] = useState(false);
    const [showModelYearDropdown, setShowModelYearDropdown] = useState(false);
    const [modelYears, setModelYears] = useState<{ name: 'string'; key: 'string' }[]>([]);
    const [carlines, setCarlines] = useState<{ name: 'string'; key: 'string' }[]>([]);
    const [referenceData, setReferenceData] = useState<ReferenceDataItem[]>([]);
    const [allEmergencyGuides, setAllEmergencyGuides] = useState<ReferenceDataItem[]>([]);
    const [isLoadingFilters, setIsLoadingFilters] = useState(true);
    const [isLoadingGuides, setIsLoadingGuides] = useState(true);
    const [currentPage, setCurrentPage] = useState(0);
    const [hasMorePages, setHasMorePages] = useState(false);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const pageSize = 10;

    const { t } = useTranslation();
    const scrollRef = useRef<HTMLDivElement | null>(null);
    const classes = useStyles();
    const publicationDispatch = usePublicationsDispatch();

    const handleCardClick = async (documentId: string) => {
        try {
            const url = await getUrl(documentId, 'view', null);
            if (url && url.length > 0) {
                window.open(url, '_blank');
            } else {
                publicationDispatch(
                    publicationsActions.setToastMessage({
                        toast: {
                            toastMessage: 'Document not found or not free',
                            iconColor: 'white',
                            iconName: 'x-close',
                        },
                    })
                );
            }
        } catch (error: any) {
            logger.error(`Failed to open the document [${documentId}]`, { error, documentId });
            const errorMessage = error?.message || error?.toString() || '';
            const displayMessage =
                errorMessage.includes('Document not found or not free') || errorMessage.includes('Validation error')
                    ? 'Document not found or not free'
                    : 'Error opening document';

            publicationDispatch(
                publicationsActions.setToastMessage({
                    toast: {
                        toastMessage: displayMessage,
                        iconColor: 'white',
                        iconName: 'x-close',
                    },
                })
            );
        }
    };

    const handleModelYearCheckbox = async (key: string) => {
        const isCurrentlySelected = selectedModelYear.includes(key);

        if (isCurrentlySelected) {
            setSelectedModelYear([]);
            setCurrentPage(0);

            if (selectedCarline.length > 0 && selectedCarline[0] !== 'all') {
                try {
                    const response = await vinSessionService.getPublicPublicationsByQuery({
                        uri: `?page=${0}&size=${pageSize}`,
                        body: {
                            carlineId: selectedCarline[0],
                            publicationTypeIds: [
                                configService.debugEnvName === 'production'
                                    ? 'c5b6d3ad-00a8-4195-be8d-690824c7c027'
                                    : configService.debugEnvName === 'stage'
                                      ? 'ce4a2de2-7890-42e1-83ae-ddd038ed9d37'
                                      : configService.debugEnvName === 'development' ||
                                          configService.debugEnvName === 'local'
                                        ? 'b7707821-66b8-436a-9cae-f021429d507c'
                                        : 'c5b6d3ad-00a8-4195-be8d-690824c7c027',
                            ],
                            searchType: 'all',
                            docStatus: 'published',
                        },
                        vin: '',
                        ignoreCache: true,
                    });

                    if (response && response.success && response.data && Array.isArray(response.data.content)) {
                        setReferenceData(
                            response.data.content.map((doc: any) => ({
                                icon: <Icon name="erg" />,
                                title: doc.title,
                                image: thumbnail,
                                version: doc.pubVersion,
                                id: doc.documentId,
                            }))
                        );
                        const currentPageNum = response.data.page?.number ?? 0;
                        const totalPages = response.data.page?.totalPages ?? 1;
                        setHasMorePages(currentPageNum + 1 < totalPages);
                    } else {
                        setReferenceData([]);
                        setHasMorePages(false);
                    }
                } catch (error) {
                    handleVinSessionError('Fetch Filtered Data Error', error);
                    setReferenceData([]);
                    setHasMorePages(false);
                }
            } else {
                setReferenceData(allEmergencyGuides);
                setHasMorePages(false);
            }
        } else {
            setSelectedModelYear([key]);
            setCurrentPage(0);

            if (selectedCarline.length > 0 && selectedCarline[0] !== 'all') {
                try {
                    const response = await vinSessionService.getPublicPublicationsByQuery({
                        uri: `?page=${0}&size=${pageSize}`,
                        body: {
                            carlineId: selectedCarline[0],
                            modelYear: key,
                            publicationTypeIds: [
                                configService.debugEnvName === 'production'
                                    ? 'c5b6d3ad-00a8-4195-be8d-690824c7c027'
                                    : configService.debugEnvName === 'stage'
                                      ? 'ce4a2de2-7890-42e1-83ae-ddd038ed9d37'
                                      : configService.debugEnvName === 'development' ||
                                          configService.debugEnvName === 'local'
                                        ? 'b7707821-66b8-436a-9cae-f021429d507c'
                                        : 'c5b6d3ad-00a8-4195-be8d-690824c7c027',
                            ],
                            searchType: 'all',
                            docStatus: 'published',
                        },
                        vin: '',
                        ignoreCache: true,
                    });

                    if (response && response.success && response.data && Array.isArray(response.data.content)) {
                        setReferenceData(
                            response.data.content.map((doc: any) => ({
                                icon: <Icon name="erg" />,
                                title: doc.title,
                                image: thumbnail,
                                version: doc.pubVersion,
                                id: doc.documentId,
                            }))
                        );
                        const currentPageNum = response.data.page?.number ?? 0;
                        const totalPages = response.data.page?.totalPages ?? 1;
                        setHasMorePages(currentPageNum + 1 < totalPages);
                    } else {
                        setReferenceData([]);
                        setHasMorePages(false);
                    }
                } catch (error) {
                    handleVinSessionError('Fetch Filtered Data Error', error);
                    setReferenceData([]);
                    setHasMorePages(false);
                }
            } else {
                // No carline selected - fetch with model year only
                try {
                    const response = await vinSessionService.getPublicPublicationsByQuery({
                        uri: `?page=${0}&size=${pageSize}`,
                        body: {
                            modelYear: key,
                            publicationTypeIds: [
                                configService.debugEnvName === 'production'
                                    ? 'c5b6d3ad-00a8-4195-be8d-690824c7c027'
                                    : configService.debugEnvName === 'stage'
                                      ? 'ce4a2de2-7890-42e1-83ae-ddd038ed9d37'
                                      : configService.debugEnvName === 'development' ||
                                          configService.debugEnvName === 'local'
                                        ? 'b7707821-66b8-436a-9cae-f021429d507c'
                                        : 'c5b6d3ad-00a8-4195-be8d-690824c7c027',
                            ],
                            searchType: 'all',
                            docStatus: 'published',
                        },
                        vin: '',
                        ignoreCache: true,
                    });

                    if (response && response.success && response.data && Array.isArray(response.data.content)) {
                        setReferenceData(
                            response.data.content.map((doc: any) => ({
                                icon: <Icon name="erg" />,
                                title: doc.title,
                                image: thumbnail,
                                version: doc.pubVersion,
                                id: doc.documentId,
                            }))
                        );
                        const currentPageNum = response.data.page?.number ?? 0;
                        const totalPages = response.data.page?.totalPages ?? 1;
                        setHasMorePages(currentPageNum + 1 < totalPages);
                    } else {
                        setReferenceData([]);
                        setHasMorePages(false);
                    }
                } catch (error) {
                    handleVinSessionError('Fetch Filtered Data Error', error);
                    setReferenceData([]);
                    setHasMorePages(false);
                }
            }
        }
    };

    const handleCarlineCheckbox = async (key: string) => {
        if (key === 'all') {
            const isCurrentlyAll = selectedCarline.includes('all');
            if (isCurrentlyAll) {
                setSelectedCarline([]);
                setSelectedModelYear([]);
                setReferenceData(allEmergencyGuides);
                setCurrentPage(0);
                setHasMorePages(false);
            } else {
                setSelectedCarline(['all']);
                setSelectedModelYear([]);
                setReferenceData(allEmergencyGuides);
                setCurrentPage(0);
                setHasMorePages(false);
            }
            return;
        }

        const isCurrentlySelected = selectedCarline.includes(key);

        if (isCurrentlySelected) {
            setSelectedCarline([]);
            setSelectedModelYear([]);
            setReferenceData(allEmergencyGuides);
            setCurrentPage(0);
            setHasMorePages(false);
        } else {
            setSelectedCarline([key]);
            setCurrentPage(0);

            try {
                const response = await vinSessionService.getPublicPublicationsByQuery({
                    uri: `?page=${0}&size=${pageSize}`,
                    body: {
                        carlineId: key,
                        modelYear: selectedModelYear.length > 0 ? selectedModelYear[0] : undefined,
                        publicationTypeIds: [
                            configService.debugEnvName === 'production'
                                ? 'c5b6d3ad-00a8-4195-be8d-690824c7c027'
                                : configService.debugEnvName === 'stage'
                                  ? 'ce4a2de2-7890-42e1-83ae-ddd038ed9d37'
                                  : configService.debugEnvName === 'development' ||
                                      configService.debugEnvName === 'local'
                                    ? 'b7707821-66b8-436a-9cae-f021429d507c'
                                    : 'c5b6d3ad-00a8-4195-be8d-690824c7c027',
                        ],
                        searchType: 'all',
                        docStatus: 'published',
                    },
                    vin: '',
                    ignoreCache: true,
                });

                if (response && response.success && response.data && Array.isArray(response.data.content)) {
                    setReferenceData(
                        response.data.content.map((doc: any) => ({
                            icon: <Icon name="erg" />,
                            title: doc.title,
                            image: thumbnail,
                            version: doc.pubVersion,
                            id: doc.documentId,
                        }))
                    );
                    const currentPageNum = response.data.page?.number ?? 0;
                    const totalPages = response.data.page?.totalPages ?? 1;
                    setHasMorePages(currentPageNum + 1 < totalPages);
                } else {
                    setReferenceData([]);
                    setHasMorePages(false);
                }
            } catch (error) {
                handleVinSessionError('Fetch Filtered Data Error', error);
                setReferenceData([]);
                setHasMorePages(false);
            }
        }
    };

    const loadMoreDocuments = async () => {
        if (!hasMorePages || isLoadingMore) return;

        setIsLoadingMore(true);
        const nextPage = currentPage + 1;

        try {
            const isAllCarlines = selectedCarline.length === 0 || selectedCarline[0] === 'all';

            const response = await vinSessionService.getPublicPublicationsByQuery({
                uri: `?page=${nextPage}&size=${pageSize}`,
                body: {
                    ...(isAllCarlines ? {} : { carlineId: selectedCarline[0] }),
                    modelYear: selectedModelYear.length > 0 ? selectedModelYear[0] : undefined,
                    publicationTypeIds: [
                        configService.debugEnvName === 'production'
                            ? 'c5b6d3ad-00a8-4195-be8d-690824c7c027'
                            : configService.debugEnvName === 'stage'
                              ? 'ce4a2de2-7890-42e1-83ae-ddd038ed9d37'
                              : configService.debugEnvName === 'development' || configService.debugEnvName === 'local'
                                ? 'b7707821-66b8-436a-9cae-f021429d507c'
                                : 'c5b6d3ad-00a8-4195-be8d-690824c7c027',
                    ],
                    searchType: 'all',
                    docStatus: 'published',
                },
                vin: '',
                ignoreCache: true,
            });

            if (response && response.success && response.data && Array.isArray(response.data.content)) {
                const newDocs = response.data.content.map((doc: any) => ({
                    icon: <Icon name="erg" />,
                    title: doc.title,
                    image: thumbnail,
                    version: doc.pubVersion,
                    id: doc.documentId,
                }));

                if (isAllCarlines) {
                    setAllEmergencyGuides((prev) => [...prev, ...newDocs]);
                }
                setReferenceData((prev) => [...prev, ...newDocs]);
                setCurrentPage(nextPage);

                const currentPageNum = response.data.page?.number ?? nextPage;
                const totalPages = response.data.page?.totalPages ?? 1;
                setHasMorePages(currentPageNum + 1 < totalPages);
            } else {
                setHasMorePages(false);
            }
        } catch (error) {
            handleVinSessionError('Load More Documents Error', error);
            setHasMorePages(false);
        } finally {
            setIsLoadingMore(false);
        }
    };

    const checkScrollButtons = () => {
        if (scrollRef.current) {
            const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
            setShowLeftButton(scrollLeft > 0);
            const isAtEnd = scrollLeft + clientWidth >= scrollWidth - 5;
            setShowRightButton(scrollLeft + clientWidth < scrollWidth - 5);

            const isNearEnd = scrollLeft + clientWidth >= scrollWidth - 300;
            if (isNearEnd && hasMorePages && !isLoadingMore) {
                loadMoreDocuments();
            }
        }
    };

    const scroll = (direction: 'left' | 'right') => {
        if (scrollRef.current) {
            const scrollAmount = 300;
            if (direction == 'left') {
                scrollRef.current.scrollLeft -= scrollAmount;
            } else {
                scrollRef.current.scrollLeft += scrollAmount;
            }
            setTimeout(checkScrollButtons, 300);
        }
    };

    useEffect(() => {
        if (!showModelYearDropdown) return;

        const handleClickOutside = (event: MouseEvent) => {
            const dropdown = document.getElementById('modelyear-dropdown');
            if (dropdown && !dropdown.contains(event.target as Node)) {
                setShowModelYearDropdown(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showModelYearDropdown]);

    useEffect(() => {
        if (!showCarlineDropdown) return;

        const handleClickOutside = (event: MouseEvent) => {
            const dropdown = document.getElementById('carline-dropdown');
            if (dropdown && !dropdown.contains(event.target as Node)) {
                setShowCarlineDropdown(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showCarlineDropdown]);

    useEffect(() => {
        const currentRef = scrollRef.current;
        if (currentRef) {
            currentRef.addEventListener('scroll', checkScrollButtons);
            checkScrollButtons();

            return () => {
                currentRef.removeEventListener('scroll', checkScrollButtons);
            };
        }
    }, [referenceData, allEmergencyGuides, hasMorePages, isLoadingMore]);

    useEffect(() => {
        if (!isLoadingGuides && scrollRef.current) {
            setTimeout(() => {
                checkScrollButtons();
            }, 100);
        }
    }, [isLoadingGuides]);

    useEffect(() => {
        const fetchModelYearsAndCarline = async () => {
            setIsLoadingFilters(true);
            try {
                const response = await docsService.fetchEmergencyFilters({});
                if (response.success && response.data.modelYear && response.data.modelYear.length > 0) {
                    const formattedYears = [
                        ...response.data.modelYear.map((year: string) => ({
                            name: year,
                            key: year,
                        })),
                    ];
                    const formattedCarlines = [
                        { name: 'All Carlines', key: 'all' },
                        ...response.data.carline.map((carline: any) => ({
                            name: carline.description,
                            key: carline.id,
                        })),
                    ];
                    setModelYears(formattedYears);
                    setCarlines(formattedCarlines);
                }
            } catch (error) {
                handleVinSessionError('Emergency Guide Filter fetch error', error);
                return;
            } finally {
                setIsLoadingFilters(false);
            }
        };
        fetchModelYearsAndCarline();
    }, []);

    useEffect(() => {
        let cancelled = false;
        const fetchAllEmergencyGuides = async () => {
            setIsLoadingGuides(true);
            try {
                const response = await vinSessionService.getPublicPublicationsByQuery({
                    uri: `?page=${0}&size=${pageSize}`,
                    body: {
                        publicationTypeIds: [
                            configService.debugEnvName === 'production'
                                ? 'c5b6d3ad-00a8-4195-be8d-690824c7c027'
                                : configService.debugEnvName === 'stage'
                                  ? 'ce4a2de2-7890-42e1-83ae-ddd038ed9d37'
                                  : configService.debugEnvName === 'development' ||
                                      configService.debugEnvName === 'local'
                                    ? 'b7707821-66b8-436a-9cae-f021429d507c'
                                    : 'c5b6d3ad-00a8-4195-be8d-690824c7c027',
                        ],
                        searchType: 'all',
                        docStatus: 'published',
                    },
                    vin: '',
                    ignoreCache: true,
                });

                if (cancelled) {
                    return;
                }

                if (response && response.success && response.data && Array.isArray(response.data.content)) {
                    const guides = response.data.content.map((doc: any) => ({
                        icon: <Icon name="erg" style={{ marginBottom: 3 }} />,
                        title: doc.title,
                        image: thumbnail,
                        version: doc.pubVersion,
                        id: doc.documentId,
                    }));

                    setAllEmergencyGuides(guides);
                    setReferenceData(guides);

                    const currentPageNum = response.data.page?.number ?? 0;
                    const totalPages = response.data.page?.totalPages ?? 1;
                    setHasMorePages(currentPageNum + 1 < totalPages);
                    setCurrentPage(0);
                }
            } catch (error) {
                if (cancelled) {
                    return;
                }
                handleVinSessionError('Emergency Guides fetch error', error);
                return;
            } finally {
                if (!cancelled) {
                    setIsLoadingGuides(false);
                }
            }
        };

        fetchAllEmergencyGuides();
        return () => {
            cancelled = true;
        };
    }, []);

    const styles = StyleSheet.create({
        title: {
            marginBottom: 16,
        },
        descriptionText: {
            fontFamily: 'Roboto',
            fontWeight: 400,
            fontSize: 18,
            letterSpacing: 0,
            color: '#4C525D',
        },
        dropdownTypography: {
            fontFamily: 'Roboto',
            fontWeight: 600,
            fontSize: 15,
            letterSpacing: 0,
            color: '#1F518F',
        },
        carlineTypographySelected: {
            color: '#1F518F',
        },
    });

    return (
        <div className={classes.root} data-testid="emergency-guides">
            <div className={classes.container}>
                <Typography variant="h1" style={{ marginBottom: 16 }}>
                    {t('EmergencyResponse:title', 'Emergency Response Guides')} | <TechShareLogo />
                </Typography>

                {carlines.length > 0 && (
                    <>
                        <div className={classes.banner}>
                            <EmergencyGuideBanner />
                        </div>

                        <div className={classes.description}>
                            <Typography style={styles.descriptionText}>
                                An Emergency Response Guide for Subaru electric and hybrid cars provides essential
                                information and procedures for first responders to safely handle emergencies involving
                                these vehicles. It includes details on safely disabling the powertrain, dealing with
                                high-voltage components, and accessing occupants.
                            </Typography>
                        </div>
                    </>
                )}

                {carlines.length > 0 && (
                    <>
                        <section className={classes.section}>
                            <Typography variant="h2">By Carline and Model Year</Typography>
                        </section>

                        {isLoadingFilters ? (
                            <div className={classes.filterRow}>
                                <div className={classes.searchContainer}>
                                    <SkeletonLoader type="text" height={32} width="100%" />
                                </div>
                                <div className={classes.dropdowns}>
                                    <SkeletonLoader type="text" height={32} width={120} />
                                    <SkeletonLoader type="text" height={32} width={120} />
                                </div>
                            </div>
                        ) : (
                            modelYears.length > 0 && (
                                <div className={classes.filterRow}>
                                    <div className={classes.searchContainer}>
                                        <input
                                            type="text"
                                            placeholder="Search by Carline, Model Year"
                                            value={searchTerm}
                                            onChange={(e) => setSearchTerm(e.target.value)}
                                            className={classes.searchInput}
                                        />
                                        <span className={classes.searchIcon}>
                                            <Icon name="magnifying-glass" />
                                        </span>
                                    </div>

                                    <div className={classes.dropdowns}>
                                        <div
                                            className={`${classes.dropdown} ${selectedCarline.length > 0 ? classes.dropdownActive : ''}`}
                                            style={{
                                                width: showCarlineDropdown ? 200 : undefined,
                                                minWidth: showCarlineDropdown ? 180 : undefined,
                                            }}
                                            id="carline-dropdown"
                                        >
                                            <div
                                                className={classes.dropdownLabel}
                                                onMouseDown={(e) => {
                                                    e.stopPropagation();
                                                    setShowCarlineDropdown((prev) => !prev);
                                                    setShowModelYearDropdown(false);
                                                }}
                                            >
                                                <Typography style={styles.dropdownTypography}>
                                                    {selectedCarline.length === 0
                                                        ? 'Carline'
                                                        : carlines.find((c) => c.key === selectedCarline[0])?.name ||
                                                          selectedCarline[0]}
                                                </Typography>
                                                <div
                                                    className={classes.dropdownArrow}
                                                    style={{
                                                        transform: `rotate(${showCarlineDropdown ? 180 : 0}deg)`,
                                                        right: showCarlineDropdown ? 8 : undefined,
                                                        left: showCarlineDropdown ? undefined : '100%',
                                                        backgroundColor:
                                                            selectedCarline.length > 0 ? '#DEE6EF' : undefined,
                                                    }}
                                                >
                                                    <Icon
                                                        name="arrow-small-down"
                                                        color="blueOne"
                                                        style={{
                                                            marginTop: 2,
                                                        }}
                                                    />
                                                </div>
                                            </div>
                                            {showCarlineDropdown && (
                                                <div className={classes.dropdownMenu}>
                                                    {carlines.map((carline) => (
                                                        <div
                                                            key={carline.key}
                                                            style={{
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                padding: '4px 0',
                                                                cursor: 'pointer',
                                                            }}
                                                            onClick={() => handleCarlineCheckbox(carline.key)}
                                                        >
                                                            <input
                                                                type="radio"
                                                                name="carline"
                                                                checked={
                                                                    selectedCarline.length > 0 &&
                                                                    selectedCarline.includes(carline.key)
                                                                }
                                                                onChange={() => {}}
                                                                style={{ marginRight: 8, cursor: 'pointer' }}
                                                                readOnly
                                                            />
                                                            {carline.name}
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>

                                        <div
                                            className={`${classes.dropdown} ${selectedModelYear.length > 0 ? classes.dropdownActive : ''}`}
                                            style={{
                                                width: showModelYearDropdown ? 200 : undefined,
                                                minWidth: showModelYearDropdown ? 180 : undefined,
                                            }}
                                            id="modelyear-dropdown"
                                        >
                                            <div
                                                className={classes.dropdownLabel}
                                                onMouseDown={(e) => {
                                                    e.stopPropagation();
                                                    setShowModelYearDropdown((prev) => !prev);
                                                    setShowCarlineDropdown(false);
                                                }}
                                            >
                                                <Typography style={styles.dropdownTypography}>
                                                    {selectedModelYear.length === 0
                                                        ? 'Model Year'
                                                        : modelYears.find((y) => y.key === selectedModelYear[0])
                                                              ?.name || selectedModelYear[0]}
                                                </Typography>
                                                <div
                                                    className={classes.dropdownArrow}
                                                    style={{
                                                        right: showModelYearDropdown ? 8 : undefined,
                                                        left: showModelYearDropdown ? undefined : '100%',
                                                        transform: `rotate(${showModelYearDropdown ? 180 : 0}deg)`,
                                                        backgroundColor:
                                                            selectedModelYear.length > 0 ? '#DEE6EF' : undefined,
                                                    }}
                                                >
                                                    <Icon
                                                        name="arrow-small-down"
                                                        color="blueOne"
                                                        style={{
                                                            marginTop: 2,
                                                        }}
                                                    />
                                                </div>
                                            </div>
                                            {showModelYearDropdown && (
                                                <div
                                                    style={{
                                                        position: 'absolute',
                                                        top: '100%',
                                                        left: 0,
                                                        background: '#fff',
                                                        border: '1px solid #ccc',
                                                        borderRadius: '4px 0 0 4px',
                                                        boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
                                                        zIndex: 10,
                                                        minWidth: 180,
                                                        padding: 8,
                                                        width: 200,
                                                    }}
                                                >
                                                    {modelYears.map((modelYear) => (
                                                        <div
                                                            key={modelYear.key}
                                                            style={{
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                padding: '4px 0',
                                                                cursor: 'pointer',
                                                            }}
                                                            onClick={() => handleModelYearCheckbox(modelYear.key)}
                                                        >
                                                            <input
                                                                type="radio"
                                                                name="modelyear"
                                                                checked={
                                                                    selectedModelYear.length > 0 &&
                                                                    selectedModelYear.includes(modelYear.key)
                                                                }
                                                                onChange={() => {}}
                                                                style={{ marginRight: 8, cursor: 'pointer' }}
                                                                readOnly
                                                            />
                                                            {modelYear.name}
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            )
                        )}
                    </>
                )}

                {carlines.length === 0 && !isLoadingFilters && !isLoadingGuides ? (
                    <div
                        style={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            minHeight: '400px',
                            textAlign: 'center',
                        }}
                    >
                        <Typography variant="h3" style={{ color: '#4C525D' }}>
                            No Public Emergency Response Guides Available
                        </Typography>
                    </div>
                ) : (
                    <div className={classes.mainContent}>
                        <div className={`${classes.carlineColumn} custom-scrollbar`}>
                            {isLoadingFilters ? (
                                <>
                                    {[...Array(6)].map((_, idx) => (
                                        <div
                                            key={idx}
                                            style={{
                                                padding: '20px 24px',
                                                borderBottom: '1px solid #E0E4EA',
                                            }}
                                        >
                                            <SkeletonLoader type="text" height={20} width="70%" />
                                        </div>
                                    ))}
                                </>
                            ) : (
                                carlines.map((carline) => {
                                    const isSelected = selectedCarline.includes(carline.key);
                                    return (
                                        <div
                                            key={carline.key}
                                            className={`${classes.carlineRow} ${isSelected ? classes.carlineRowSelected : ''}`}
                                            onClick={() => handleCarlineCheckbox(carline.key)}
                                        >
                                            <Typography
                                                style={{
                                                    fontWeight: 500,
                                                    fontSize: 17,
                                                    color: isSelected ? '#1F518F' : '#4C525D',
                                                }}
                                            >
                                                {carline.name}
                                            </Typography>
                                            <div className={classes.carlineIcon}>
                                                <Icon
                                                    name="arrow-small-up"
                                                    color={isSelected ? 'blueOne' : 'textDarkTertiary'}
                                                    style={{ marginLeft: 8 }}
                                                />
                                            </div>
                                        </div>
                                    );
                                })
                            )}
                        </div>

                        <div className={classes.rightColumn}>
                            {isLoadingGuides ? (
                                <div style={{ position: 'relative', width: '100%' }}>
                                    <div className={classes.referenceCardContainer}>
                                        {[...Array(3)].map((_, idx) => (
                                            <div
                                                key={idx}
                                                style={{
                                                    minWidth: 265,
                                                    width: 265,
                                                    maxWidth: 265,
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    gap: 12,
                                                }}
                                            >
                                                <SkeletonLoader type="text" height={120} width={265} />
                                                <SkeletonLoader type="text" height={16} width="80%" />
                                                <SkeletonLoader type="text" height={14} width="40%" />
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ) : (
                                <div style={{ position: 'relative' }}>
                                    <div className={classes.referenceCardContainer} ref={scrollRef}>
                                        {showLeftButton && (
                                            <div className={`${classes.gradientOverlay} ${classes.gradientLeft}`}></div>
                                        )}
                                        {showLeftButton && (
                                            <button
                                                className={`${classes.scrollButton} ${classes.scrollButtonLeft}`}
                                                onClick={() => scroll('left')}
                                                aria-label="left"
                                            >
                                                <Icon name="chevron-left" color="white" />
                                            </button>
                                        )}
                                        {showRightButton && (
                                            <div
                                                className={`${classes.gradientOverlay} ${classes.gradientRight}`}
                                            ></div>
                                        )}
                                        {showRightButton && (
                                            <button
                                                className={`${classes.scrollButton} ${classes.scrollButtonRight}`}
                                                onClick={() => scroll('right')}
                                                aria-label="right"
                                            >
                                                <Icon name="chevron-right" color="white" />
                                            </button>
                                        )}
                                        {(referenceData.length > 0 ? referenceData : allEmergencyGuides).map(
                                            (item, idx) => (
                                                <QuickReferenceCard
                                                    key={idx}
                                                    Icon={item.icon}
                                                    title={item.title}
                                                    image={item.image}
                                                    version={item.version}
                                                    onClick={() => handleCardClick(item.id)}
                                                />
                                            )
                                        )}
                                        {isLoadingMore && (
                                            <>
                                                <SkeletonLoader type="text" height={200} width={265} />
                                                <SkeletonLoader type="text" height={200} width={265} />
                                                <SkeletonLoader type="text" height={200} width={265} />
                                            </>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};
